import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {NbWindowRef} from '@nebular/theme';
import {ApiResponse, BaseComponent, CartProduct, CartRemoteService, CustomerGroup, DeliveryAddress, NbRadioOption, OrderForm, OrderInfo, OrderService, PickupAddress, SourceOrderProduct, StoreService} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {catchError, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {PopupAddressComponent} from '../../private/company-module/components/popup-address/popup-address.component';

@Component({
  selector: 'app-specification-copy',
  templateUrl: './popup-offer.component.html',
  styleUrls: ['./popup-offer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PopupOfferComponent extends BaseComponent implements OnInit {

  fail = false;
  submitted = false;
  deliveryMethod = '1';
  pickupAddress: string;
  deliveryAddress: string;
  orderInfo: OrderInfo;
  source: SourceOrderProduct;
  sourceId: number;
  totalCost: number;
  totalCostVAT: number;
  products: CartProduct[];

  deliveryMethodOptions: NbRadioOption[] = [
    {value: '2', label: 'Самовывоз'},
    {value: '1', label: 'Доставка по адресу'}
  ];

  pickupAddresses$: Observable<PickupAddress[]>;
  deliveryAddresses$: Observable<DeliveryAddress[]>;
  selectedAddress: any = null;

  inputForm = new UntypedFormGroup({
    deliveryMethod: new UntypedFormControl(this.deliveryMethod),
    pickupAddress: new UntypedFormControl('2'),
    deliveryAddress: new UntypedFormControl(),
    comment: new UntypedFormControl()
  });

  readonly customerGroup$ = this.storeService.getCustomerGroup();

  constructor(private readonly storeService: StoreService,
              private readonly orderService: OrderService,
              private readonly windowDialog: NbWindowRef,
              private readonly cartService: CartRemoteService) {
    super();
  }

  ngOnInit(): void {

    this.pickupAddresses$ = this.customerGroup$.pipe(
      tap(_ => this.loading = true),
      switchMap(response => this.getPickupAddresses(response)),
      tap(addressList => this.setDefaultSelectedAdress(addressList)),
      shareReplay(),
      tap(_ => this.loading = false)
    );

    this.deliveryAddresses$ = this.getDeliveryAddresses().pipe(
      tap(_ => this.loading = true),
      shareReplay(),
      tap(_ => this.loading = false)
    );

  }

  setDefaultSelectedAdress(addressList: PickupAddress[]): void {
    if (addressList.length > 0) {
      this.inputForm.controls['pickupAddress'].setValue(addressList[0].id);
      this.cdr.detectChanges();
    }
  }

  getPickupAddresses(customerGroup: CustomerGroup): Observable<PickupAddress[]> {
    return this.orderService.getPickupAddresses(customerGroup).pipe(map(response => response.data));
  }

  getDeliveryAddresses(): Observable<DeliveryAddress[]> {
    const user = JSON.parse(localStorage.getItem('user'));
    const contractId = user?.contractId;
    return this.orderService.getDeliveryAddresses(contractId).pipe(map(response => response.data));
  }

  openDeliveryAddress() {
    this.windowService.open(PopupAddressComponent, {
      context: {
        refresh: false
      }
    }).onClose.subscribe(data => {

      this.selectedAddress = data;

      this.inputForm.controls['deliveryAddress'].setValue(data.id);
      this.inputForm.controls['deliveryMethod'].setValue('');

      setTimeout(_ => {
        this.inputForm.controls['deliveryMethod'].setValue('1');
      }, 1);

    });
  }

  isPickup() {
    return this.inputForm.value.deliveryMethod === '2';
  }

  isDelivery() {
    return this.inputForm.value.deliveryMethod === '1';
  }

  onSubmit() {
    if (!this.validForm()) {
      return false;
    }
    this.submitted = true;
    const pickup = this.inputForm.controls['deliveryMethod'].value === '2';

    const orderForm: OrderForm = {
      deliveryMethod: parseInt(this.inputForm.value.deliveryMethod, 10),
      // Send only the address the chosen method uses — the pickup control carries a hardcoded
      // default that must not travel with a delivery order.
      deliveryAddress: pickup || !this.inputForm.value.deliveryAddress ?
        0 : parseInt(this.inputForm.value.deliveryAddress, 10),
      pickupAddress: !pickup || !this.inputForm.value.pickupAddress ?
        0 : parseInt(this.inputForm.value.pickupAddress, 10),
      comment: this.inputForm.value.comment,
      source: this.source,
      sourceId: this.sourceId,
      cart: this.products?.length ? this.products.map(item => item.id) : []
    };

    this.orderService.create(orderForm).pipe(
      tap((response: ApiResponse<OrderInfo>) => this.handleResponse(response)),
      take(1),
      catchError(err => this.handleError(err, true)),
    ).subscribe();
  }

  private handleResponse(response: ApiResponse<OrderInfo>) {
    if (response.success) {
      this.fail = false;
      this.orderInfo = response.data;
      if (this.source === SourceOrderProduct.ORDER_PART) {
        this.products.forEach(item => {
          this.cartService.remove(item).pipe(take(1)).subscribe();
        });
      }
      if (this.source === SourceOrderProduct.CART) {
        this.cartService.clear().pipe(take(1)).subscribe();
      }
    } else {
      this.fail = true;
    }

    this.submitted = false;
    this.cdr.detectChanges();
  }

  validForm() {
    // Read the form controls, not the [(ngModel)] mirrors: the mirrors are not updated when a value
    // is set on the control alone (e.g. the pickup default), so they under-report validity.
    const method = this.inputForm.controls['deliveryMethod'].value;
    const pickup = method === '2' && !!this.inputForm.controls['pickupAddress'].value;
    const delivery = method === '1' && !!this.inputForm.controls['deliveryAddress'].value;

    return pickup || delivery;
  }

  close() {
    this.windowDialog.close();
  }

  go() {
    this.router.navigate(['cabinet', 'reports', 'orders', String(this.orderInfo.id)]).then(_ => this.windowDialog.close());
  }
}
